import { ArrowLongRightIcon } from "@heroicons/react/24/outline";
import { ESubscriptionType } from "common/enums/Subscription/ESubscriptionType";
import { EDemembrementType } from "common/enums/ProductSubscribed/EDemembrementType";
import { ESubscriptionProductType } from "common/enums/ProductSubscribed/ESubscriptionProductType";
import { getViagerRatio } from "utils/baremeViager";
import ClientSheetLightResponseResource from "common/resources/ClientSheet/ClientSheetLightResponseResource";
import SubscriptionCorporationCreateRequestResource from "common/resources/Subscription/SubscriptionCorporationCreateRequestResource";
import Button, { EButtonVariant } from "components/elements/Button";
import Typography, { ITypo } from "components/elements/Typography";
import I18n from "components/materials/I18n";
import PageTemplateSubscriptions from "components/PageTemplateSubscriptions";
import ModuleConfig from "configs/ModuleConfig";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SubscriptionService from "services/SubscriptionService";
import { container } from "tsyringe";
import NumberUtils from "utils/NumberUtils";

import classes from "./classes.module.scss";
import ProductSelectedTable from "./ProductSelectedTable";
import ProductSelectorTable, { IProductSelected } from "./ProductSelectorTable";

const subscriptionService = container.resolve(SubscriptionService);
const modules = container.resolve(ModuleConfig).get().modules;

export default function SelectProductsCorporation() {
	const { corporationSheetId } = useParams();
	const [productsInSubscription, setProductsInSubscription] = useState<IProductSelected[]>([]);
	const [client] = useState<ClientSheetLightResponseResource | null>(null);
	const navigate = useNavigate();

	const onCreateSubscription = useCallback(() => {
		if (!corporationSheetId) return console.warn("No corporationSheedId provided");
		subscriptionService
			.postCorporation(
				SubscriptionCorporationCreateRequestResource.hydrate<SubscriptionCorporationCreateRequestResource>({
					productsSubscribed: productsInSubscription.map((product) => product.dto),
					subscriptionType: ESubscriptionType.legalPerson,
				}),
			)
			.then((subscription) =>
				navigate(modules.pages.Subscriptions.props.pages.SubscriptionQuestionsCorporation.pages.PaymentMethods.props.path.replace(":subscriptionId", subscription.id)),
			)
			.catch(() => console.warn("Subscription not created"));
	}, [corporationSheetId, productsInSubscription, navigate]);

	const onCancel = useCallback(() => navigate(modules.pages.Subscriptions.props.pages.InProgress.props.path), [navigate]);

	// Navigate to home if corporationSheedId is not provided
	if (!corporationSheetId) {
		console.warn("No corporationSheedId provided");
		return null;
	}

	return (
		<PageTemplateSubscriptions
			mainPageTitle={I18n.asset.pages.subscriptions.select_products.page_title}
			headerTitle={I18n.asset.pages.subscriptions.select_products.title}
			overridePreviousClick={{ onClick: () => navigate(modules.pages.Subscriptions.props.pages.SubscriptionExistingCorporation.props.path) }}>
			<div className={classes["root"]}>
				<Typography className={classes["subtitle"]} typo={ITypo.P_MEDIUM_BOLD}>
					{I18n.asset.pages.subscriptions.select_products.subtitle.replace("$$user_name$$", client?.firstName + " " + client?.lastName)}
				</Typography>
				<div className={classes["table-container"]}>
					<ProductSelectorTable
						productsInSubscription={productsInSubscription}
						setProductsInSubscription={setProductsInSubscription}
						corporationSubscriberId={corporationSheetId}
					/>
					<div className={classes["separator"]} />
					<ProductSelectedTable productsInSubscription={productsInSubscription} setProductsInSubscription={setProductsInSubscription} />
				</div>
				<div className={classes["total-container"]}>
					<Typography typo={ITypo.P_MEDIUM_BOLD} className={classes["total-label"]}>
						{I18n.asset.pages.subscriptions.select_products.total_amount}
					</Typography>
					<Typography typo={ITypo.P_MEDIUM_BOLD} className={classes["total-number"]}>
						{NumberUtils.formatNumberAddingSpaces(productsInSubscription.reduce((acc, product) => {
							const { subscriptionProductType, demembrementDuration, demembrementType, viagerAge } = product.dto;
							let ratio = 1;
							if (subscriptionProductType !== ESubscriptionProductType.pleine_propriete) {
								if (demembrementType === EDemembrementType.viager && viagerAge !== undefined && viagerAge !== null) {
									ratio = getViagerRatio(viagerAge, subscriptionProductType) ?? 1;
								} else if (demembrementDuration) {
									const key = product.product.demembrementKeys?.find((k) => k.duration === demembrementDuration);
									if (key) ratio = subscriptionProductType === ESubscriptionProductType.usufruit ? key.usufruitRatio : key.nueProprieteRatio;
								}
							}
							return acc + product.dto.quantity * product.product.price * ratio;
						}, 0) ?? 0) + " €"}
					</Typography>
				</div>

				<div className={classes["button-container"]}>
					<Button variant={EButtonVariant.OUTLINED} onClick={onCancel}>
						{I18n.asset.common.cancel}
					</Button>
					<Button onClick={onCreateSubscription} disabled={!productsInSubscription.length}>
						{I18n.asset.pages.subscriptions.select_products.next_page}
						<ArrowLongRightIcon />
					</Button>
				</div>
			</div>
		</PageTemplateSubscriptions>
	);
}
